/**
 * Minimal hand-rolled protobuf encoder/decoder for GuruConnect shell messages.
 *
 * Encodes/decodes only the shell-related subset of guruconnect.proto:
 *   ShellRequest  (Message field 90)
 *   ShellInput    (Message field 91)
 *   ShellOutput   (Message field 92)
 *   ShellResize   (Message field 93)
 *   ShellClose    (Message field 94)
 *
 * This avoids pulling in a protobuf JS library. The wire format is simple
 * enough for these messages that a hand-rolled codec is reliable and small.
 */

import type { ShellType } from "./ShellPanel";

// ---------------------------------------------------------------------------
// Encoding primitives
// ---------------------------------------------------------------------------

function encodeVarint(value: number): number[] {
  const bytes: number[] = [];
  let v = value >>> 0; // unsigned
  while (v > 0x7f) {
    bytes.push((v & 0x7f) | 0x80);
    v >>>= 7;
  }
  bytes.push(v & 0x7f);
  return bytes;
}

function encodeBytes(fieldNumber: number, data: Uint8Array): Uint8Array {
  const tag = encodeVarint((fieldNumber << 3) | 2);
  const len = encodeVarint(data.length);
  const result = new Uint8Array(tag.length + len.length + data.length);
  result.set(tag, 0);
  result.set(len, tag.length);
  result.set(data, tag.length + len.length);
  return result;
}

function encodeString(fieldNumber: number, value: string): Uint8Array {
  return encodeBytes(fieldNumber, new TextEncoder().encode(value));
}

function encodeVarintField(fieldNumber: number, value: number): Uint8Array {
  const tag = encodeVarint((fieldNumber << 3) | 0);
  const val = encodeVarint(value);
  const result = new Uint8Array(tag.length + val.length);
  result.set(tag, 0);
  result.set(val, tag.length);
  return result;
}

function concat(...arrays: Uint8Array[]): Uint8Array {
  const total = arrays.reduce((acc, a) => acc + a.length, 0);
  const result = new Uint8Array(total);
  let offset = 0;
  for (const a of arrays) {
    result.set(a, offset);
    offset += a.length;
  }
  return result;
}

// ---------------------------------------------------------------------------
// Decoding primitives
// ---------------------------------------------------------------------------

function decodeVarint(buf: Uint8Array, offset: number): [number, number] {
  let value = 0;
  let shift = 0;
  let pos = offset;
  while (pos < buf.length) {
    const b = buf[pos++];
    value |= (b & 0x7f) << shift;
    if ((b & 0x80) === 0) return [value >>> 0, pos];
    shift += 7;
    if (shift > 35) break;
  }
  return [value >>> 0, pos];
}

// ---------------------------------------------------------------------------
// Encoders
// ---------------------------------------------------------------------------

/**
 * Encode Message { shell_request (90) = ShellRequest { ... } }
 */
export function encodeShellRequest(
  shellId: string,
  shellType: ShellType,
  cols: number,
  rows: number,
): Uint8Array {
  const inner = concat(
    encodeString(1, shellId),
    encodeVarintField(2, shellType === "cmd" ? 1 : 0),
    encodeVarintField(3, cols),
    encodeVarintField(4, rows),
  );
  return encodeBytes(90, inner);
}

/**
 * Encode Message { shell_input (91) = ShellInput { ... } }
 */
export function encodeShellInput(shellId: string, data: string): Uint8Array {
  const inner = concat(
    encodeString(1, shellId),
    encodeBytes(2, new TextEncoder().encode(data)),
  );
  return encodeBytes(91, inner);
}

/**
 * Encode Message { shell_resize (93) = ShellResize { ... } }
 */
export function encodeShellResize(
  shellId: string,
  cols: number,
  rows: number,
): Uint8Array {
  const inner = concat(
    encodeString(1, shellId),
    encodeVarintField(2, cols),
    encodeVarintField(3, rows),
  );
  return encodeBytes(93, inner);
}

/**
 * Encode Message { shell_close (94) = ShellClose { ... } }
 */
export function encodeShellClose(shellId: string, reason: string): Uint8Array {
  const inner = concat(encodeString(1, shellId), encodeString(2, reason));
  return encodeBytes(94, inner);
}

// ---------------------------------------------------------------------------
// Decoders
// ---------------------------------------------------------------------------

export type ShellMessage =
  | { type: "output"; shellId: string; data: Uint8Array }
  | { type: "close"; shellId: string; reason: string; exitCode: number };

/**
 * Try to extract a ShellOutput or ShellClose from a raw protobuf Message.
 * Returns null for non-shell messages (video frames, input, etc.).
 */
export function decodeShellMessage(buf: Uint8Array): ShellMessage | null {
  let offset = 0;
  while (offset < buf.length) {
    const [tag, newOffset] = decodeVarint(buf, offset);
    offset = newOffset;
    const fieldNumber = tag >>> 3;
    const wireType = tag & 0x7;

    if (wireType === 2) {
      const [len, dataOffset] = decodeVarint(buf, offset);
      const fieldData = buf.slice(dataOffset, dataOffset + len);
      offset = dataOffset + len;

      if (fieldNumber === 92) return decodeOutput(fieldData);
      if (fieldNumber === 94) return decodeClose(fieldData);
    } else if (wireType === 0) {
      const [, newOff] = decodeVarint(buf, offset);
      offset = newOff;
    } else {
      break;
    }
  }
  return null;
}

function decodeOutput(buf: Uint8Array): ShellMessage {
  let shellId = "";
  let data = new Uint8Array(0);
  let offset = 0;

  while (offset < buf.length) {
    const [tag, newOffset] = decodeVarint(buf, offset);
    offset = newOffset;
    const wireType = tag & 0x7;
    const fieldNumber = tag >>> 3;

    if (wireType === 2) {
      const [len, dataOffset] = decodeVarint(buf, offset);
      const fieldData = buf.slice(dataOffset, dataOffset + len);
      offset = dataOffset + len;
      if (fieldNumber === 1) shellId = new TextDecoder().decode(fieldData);
      else if (fieldNumber === 2) data = fieldData;
    } else if (wireType === 0) {
      const [, newOff] = decodeVarint(buf, offset);
      offset = newOff;
    } else {
      break;
    }
  }
  return { type: "output", shellId, data };
}

function decodeClose(buf: Uint8Array): ShellMessage {
  let shellId = "";
  let reason = "";
  let exitCode = 0;
  let offset = 0;

  while (offset < buf.length) {
    const [tag, newOffset] = decodeVarint(buf, offset);
    offset = newOffset;
    const wireType = tag & 0x7;
    const fieldNumber = tag >>> 3;

    if (wireType === 2) {
      const [len, dataOffset] = decodeVarint(buf, offset);
      const fieldData = buf.slice(dataOffset, dataOffset + len);
      offset = dataOffset + len;
      if (fieldNumber === 1) shellId = new TextDecoder().decode(fieldData);
      else if (fieldNumber === 2) reason = new TextDecoder().decode(fieldData);
    } else if (wireType === 0) {
      const [val, newOff] = decodeVarint(buf, offset);
      offset = newOff;
      if (fieldNumber === 3) exitCode = val;
    } else {
      break;
    }
  }
  return { type: "close", shellId, reason, exitCode };
}
